import { supabase } from '../lib/supabase';


/**
 * Get all brand safety rules for the current user
 */
export async function getBrandSafetyRules(brandId = null) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      ?.from('brand_safety_rules')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (brandId) {
      query = query?.eq('brand_id', brandId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(rule => ({
      id: rule?.id,
      userId: rule?.user_id,
      brandId: rule?.brand_id,
      ruleName: rule?.rule_name,
      avoidControversial: rule?.avoid_controversial,
      sentimentAnalysisRequired: rule?.sentiment_analysis_required,
      competitorMonitoring: rule?.competitor_monitoring,
      blacklistKeywords: rule?.blacklist_keywords || [],
      allowedSentiments: rule?.allowed_sentiments || [],
      blockedSentiments: rule?.blocked_sentiments || [],
      allowedIntents: rule?.allowed_intents || [],
      blockedIntents: rule?.blocked_intents || [],
      minSafetyScore: rule?.min_safety_score,
      isActive: rule?.is_active,
      createdAt: rule?.created_at,
      updatedAt: rule?.updated_at
    })) || [];
  } catch (error) {
    console.error('Error fetching brand safety rules:', error);
    throw error;
  }
}

/**
 * Create a new brand safety rule
 */
export async function createBrandSafetyRule(ruleData) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('brand_safety_rules')
      ?.insert({
        user_id: user?.id,
        brand_id: ruleData?.brandId,
        rule_name: ruleData?.ruleName,
        avoid_controversial: ruleData?.avoidControversial,
        sentiment_analysis_required: ruleData?.sentimentAnalysisRequired,
        competitor_monitoring: ruleData?.competitorMonitoring,
        blacklist_keywords: ruleData?.blacklistKeywords || [],
        allowed_sentiments: ruleData?.allowedSentiments || [],
        blocked_sentiments: ruleData?.blockedSentiments || [],
        allowed_intents: ruleData?.allowedIntents || [],
        blocked_intents: ruleData?.blockedIntents || [],
        min_safety_score: ruleData?.minSafetyScore || 70
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      ruleName: data?.rule_name,
      minSafetyScore: data?.min_safety_score
    };
  } catch (error) {
    console.error('Error creating brand safety rule:', error);
    throw error;
  }
}

/**
 * Get all automation rules for the current user
 */
export async function getAutomationRules(brandId = null) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      ?.from('automation_rules')
      ?.select(`
        *,
        brand_safety_rule:brand_safety_rules(id, rule_name, min_safety_score)
      `)
      ?.eq('user_id', user?.id)
      ?.order('priority', { ascending: false });

    if (brandId) {
      query = query?.eq('brand_id', brandId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(rule => ({
      id: rule?.id,
      userId: rule?.user_id,
      brandId: rule?.brand_id,
      brandSafetyRuleId: rule?.brand_safety_rule_id,
      brandSafetyRuleName: rule?.brand_safety_rule?.rule_name,
      ruleName: rule?.rule_name,
      description: rule?.description,
      triggerType: rule?.trigger_type,
      triggerConditions: rule?.trigger_conditions,
      triggerSentiments: rule?.trigger_sentiments || [],
      triggerIntents: rule?.trigger_intents || [],
      triggerResponseOpportunities: rule?.trigger_response_opportunities || [],
      triggerKeywords: rule?.trigger_keywords || [],
      autoPostEnabled: rule?.auto_post_enabled,
      requiresApproval: rule?.requires_approval,
      aiProvider: rule?.ai_provider,
      brandVoice: rule?.brand_voice,
      priority: rule?.priority,
      dailyLimit: rule?.daily_limit,
      isActive: rule?.is_active,
      createdAt: rule?.created_at,
      updatedAt: rule?.updated_at
    })) || [];
  } catch (error) {
    console.error('Error fetching automation rules:', error);
    throw error;
  }
}

/**
 * Create a new automation rule
 */
export async function createAutomationRule(ruleData) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('automation_rules')
      ?.insert({
        user_id: user?.id,
        brand_id: ruleData?.brandId,
        brand_safety_rule_id: ruleData?.brandSafetyRuleId,
        rule_name: ruleData?.ruleName,
        description: ruleData?.description,
        trigger_type: ruleData?.triggerType,
        trigger_sentiments: ruleData?.triggerSentiments || [],
        trigger_intents: ruleData?.triggerIntents || [],
        trigger_response_opportunities: ruleData?.triggerResponseOpportunities || [],
        trigger_keywords: ruleData?.triggerKeywords || [],
        auto_post_enabled: ruleData?.autoPostEnabled || false,
        requires_approval: ruleData?.requiresApproval !== false,
        ai_provider: ruleData?.aiProvider || 'openai',
        brand_voice: ruleData?.brandVoice || 'professional',
        priority: ruleData?.priority || 5,
        daily_limit: ruleData?.dailyLimit || 50
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      ruleName: data?.rule_name,
      triggerType: data?.trigger_type
    };
  } catch (error) {
    console.error('Error creating automation rule:', error);
    throw error;
  }
}

/**
 * Update an automation rule
 */
export async function updateAutomationRule(ruleId, updates) {
  try {
    const { data, error } = await supabase
      ?.from('automation_rules')
      ?.update({
        rule_name: updates?.ruleName,
        description: updates?.description,
        trigger_sentiments: updates?.triggerSentiments,
        trigger_intents: updates?.triggerIntents,
        trigger_response_opportunities: updates?.triggerResponseOpportunities,
        auto_post_enabled: updates?.autoPostEnabled,
        requires_approval: updates?.requiresApproval,
        is_active: updates?.isActive,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', ruleId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      ruleName: data?.rule_name,
      isActive: data?.is_active
    };
  } catch (error) {
    console.error('Error updating automation rule:', error);
    throw error;
  }
}

/**
 * Validate comment against brand safety rules
 */
export function validateBrandSafety(comment, safetyRule) {
  const flags = [];
  let score = 100;

  // Check blacklist keywords
  const commentText = comment?.commentText?.toLowerCase();
  const blacklistMatches = safetyRule?.blacklistKeywords?.filter(keyword => 
    commentText?.includes(keyword?.toLowerCase())
  );
  
  if (blacklistMatches?.length > 0) {
    flags?.push(`Blacklist keywords found: ${blacklistMatches?.join(', ')}`);
    score -= 30;
  }

  // Check sentiment
  if (safetyRule?.blockedSentiments?.includes(comment?.sentiment)) {
    flags?.push(`Blocked sentiment: ${comment?.sentiment}`);
    score -= 20;
  }

  // Check intent
  if (safetyRule?.blockedIntents?.includes(comment?.intent)) {
    flags?.push(`Blocked intent: ${comment?.intent}`);
    score -= 20;
  }

  // Check if sentiment is allowed
  if (safetyRule?.allowedSentiments?.length > 0 && 
      !safetyRule?.allowedSentiments?.includes(comment?.sentiment)) {
    flags?.push(`Sentiment not in allowed list: ${comment?.sentiment}`);
    score -= 15;
  }

  // Check if intent is allowed
  if (safetyRule?.allowedIntents?.length > 0 && 
      !safetyRule?.allowedIntents?.includes(comment?.intent)) {
    flags?.push(`Intent not in allowed list: ${comment?.intent}`);
    score -= 15;
  }

  return {
    passed: score >= safetyRule?.minSafetyScore,
    score: Math.max(0, score),
    flags
  };
}

/**
 * Check if comment matches automation rule triggers
 */
export function matchesAutomationTriggers(comment, rule) {
  // Check sentiment triggers
  if (rule?.triggerSentiments?.length > 0 && 
      !rule?.triggerSentiments?.includes(comment?.sentiment)) {
    return false;
  }

  // Check intent triggers
  if (rule?.triggerIntents?.length > 0 && 
      !rule?.triggerIntents?.includes(comment?.intent)) {
    return false;
  }

  // Check response opportunity triggers
  if (rule?.triggerResponseOpportunities?.length > 0 && 
      !rule?.triggerResponseOpportunities?.includes(comment?.responseOpportunity)) {
    return false;
  }

  // Check keyword triggers
  if (rule?.triggerKeywords?.length > 0) {
    const commentText = comment?.commentText?.toLowerCase();
    const hasKeyword = rule?.triggerKeywords?.some(keyword => 
      commentText?.includes(keyword?.toLowerCase())
    );
    if (!hasKeyword) return false;
  }

  return true;
}

/**
 * Get pending response approvals
 */
export async function getPendingApprovals(filters = {}) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      ?.from('response_approvals')
      ?.select(`
        *,
        comment:incoming_comments(*),
        automation_rule:automation_rules(id, rule_name),
        assigned_user:assigned_to(id, full_name, email)
      `)
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.assignedTo) {
      query = query?.eq('assigned_to', filters?.assignedTo);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(approval => ({
      id: approval?.id,
      userId: approval?.user_id,
      commentId: approval?.comment_id,
      comment: approval?.comment ? {
        id: approval?.comment?.id,
        commentText: approval?.comment?.comment_text,
        commenterName: approval?.comment?.commenter_name,
        commenterHandle: approval?.comment?.commenter_handle,
        platform: approval?.comment?.platform,
        sentiment: approval?.comment?.sentiment,
        intent: approval?.comment?.intent,
        responseOpportunity: approval?.comment?.response_opportunity
      } : null,
      automationRuleId: approval?.automation_rule_id,
      automationRuleName: approval?.automation_rule?.rule_name,
      suggestedResponse: approval?.suggested_response,
      finalResponse: approval?.final_response,
      status: approval?.status,
      assignedTo: approval?.assigned_to,
      assignedUserName: approval?.assigned_user?.full_name,
      approvedBy: approval?.approved_by,
      approvalNotes: approval?.approval_notes,
      rejectionReason: approval?.rejection_reason,
      approvedAt: approval?.approved_at,
      rejectedAt: approval?.rejected_at,
      autoPosted: approval?.auto_posted,
      postedAt: approval?.posted_at,
      postError: approval?.post_error,
      safetyScore: approval?.safety_score,
      safetyFlags: approval?.safety_flags || [],
      createdAt: approval?.created_at,
      updatedAt: approval?.updated_at
    })) || [];
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    throw error;
  }
}

/**
 * Create a response approval
 */
export async function createResponseApproval(approvalData) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('response_approvals')
      ?.insert({
        user_id: user?.id,
        comment_id: approvalData?.commentId,
        automation_rule_id: approvalData?.automationRuleId,
        suggested_response: approvalData?.suggestedResponse,
        status: approvalData?.status || 'pending',
        assigned_to: approvalData?.assignedTo || user?.id,
        safety_score: approvalData?.safetyScore,
        safety_flags: approvalData?.safetyFlags || []
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      suggestedResponse: data?.suggested_response
    };
  } catch (error) {
    console.error('Error creating response approval:', error);
    throw error;
  }
}

/**
 * Approve a response and optionally post it
 */
export async function approveResponse(approvalId, finalResponse, autoPost = false) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {
      status: 'approved',
      final_response: finalResponse,
      approved_by: user?.id,
      approved_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString()
    };

    if (autoPost) {
      updateData.auto_posted = true;
      updateData.posted_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase
      ?.from('response_approvals')
      ?.update(updateData)
      ?.eq('id', approvalId)
      ?.select()
      ?.single();

    if (error) throw error;

    // If auto-posting, also update the incoming_comments table
    if (autoPost) {
      await supabase
        ?.from('incoming_comments')
        ?.update({
          has_responded: true,
          responded_at: new Date()?.toISOString(),
          response_text: finalResponse
        })
        ?.eq('id', data?.comment_id);
    }

    return {
      id: data?.id,
      status: data?.status,
      autoPosted: data?.auto_posted
    };
  } catch (error) {
    console.error('Error approving response:', error);
    throw error;
  }
}

/**
 * Reject a response approval
 */
export async function rejectResponse(approvalId, rejectionReason) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('response_approvals')
      ?.update({
        status: 'rejected',
        rejection_reason: rejectionReason,
        approved_by: user?.id,
        rejected_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', approvalId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status
    };
  } catch (error) {
    console.error('Error rejecting response:', error);
    throw error;
  }
}

/**
 * Log automation action
 */
export async function logAutomationAction(logData) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      ?.from('automation_logs')
      ?.insert({
        user_id: user?.id,
        automation_rule_id: logData?.automationRuleId,
        comment_id: logData?.commentId,
        response_approval_id: logData?.responseApprovalId,
        action: logData?.action,
        status: logData?.status,
        details: logData?.details || {},
        error_message: logData?.errorMessage
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error logging automation action:', error);
  }
}

export const automatedResponseService = {
  /**
   * Get response approvals with optional filters
   */
  async getApprovals(options = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { status = null, platform = null } = options;

      let query = supabase
        ?.from('response_approvals')
        ?.select(`
          *,
          comment:incoming_comments(id, commenter_name, commenter_handle, comment_text, platform, sentiment, intent, response_opportunity),
          automation_rule:automation_rules(id, rule_name)
        `)
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      if (platform) {
        query = query?.eq('comment.platform', platform);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(approval => ({
        id: approval?.id,
        userId: approval?.user_id,
        commentId: approval?.comment_id,
        automationRuleId: approval?.automation_rule_id,
        automationRuleName: approval?.automation_rule?.rule_name,
        suggestedResponse: approval?.suggested_response,
        finalResponse: approval?.final_response,
        status: approval?.status,
        safetyScore: approval?.safety_score,
        safetyFlags: approval?.safety_flags || [],
        approvedBy: approval?.approved_by,
        approvedAt: approval?.approved_at,
        rejectedBy: approval?.rejected_by,
        rejectedAt: approval?.rejected_at,
        rejectionReason: approval?.rejection_reason,
        autoPosted: approval?.auto_posted,
        metadata: approval?.metadata,
        createdAt: approval?.created_at,
        comment: approval?.comment ? {
          id: approval?.comment?.id,
          commenterName: approval?.comment?.commenter_name,
          commenterHandle: approval?.comment?.commenter_handle,
          commentText: approval?.comment?.comment_text,
          platform: approval?.comment?.platform,
          sentiment: approval?.comment?.sentiment,
          intent: approval?.comment?.intent,
          responseOpportunity: approval?.comment?.response_opportunity
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching approvals:', error);
      throw error;
    }
  },

  /**
   * Approve a response
   */
  async approveResponse(approvalId, finalResponse, autoPost = false) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('response_approvals')
        ?.update({
          status: 'approved',
          final_response: finalResponse,
          approved_by: user?.id,
          approved_at: new Date()?.toISOString(),
          auto_posted: autoPost
        })
        ?.eq('id', approvalId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        finalResponse: data?.final_response
      };
    } catch (error) {
      console.error('Error approving response:', error);
      throw error;
    }
  },

  /**
   * Reject a response
   */
  async rejectResponse(approvalId, reason) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('response_approvals')
        ?.update({
          status: 'rejected',
          rejected_by: user?.id,
          rejected_at: new Date()?.toISOString(),
          rejection_reason: reason
        })
        ?.eq('id', approvalId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status
      };
    } catch (error) {
      console.error('Error rejecting response:', error);
      throw error;
    }
  },

  /**
   * Update an approval
   */
  async updateApproval(approvalId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('response_approvals')
        ?.update({
          suggested_response: updates?.suggestedResponse,
          final_response: updates?.finalResponse,
          safety_score: updates?.safetyScore,
          safety_flags: updates?.safetyFlags
        })
        ?.eq('id', approvalId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        suggestedResponse: data?.suggested_response
      };
    } catch (error) {
      console.error('Error updating approval:', error);
      throw error;
    }
  }
};